import React from "react";
import { useLocation } from "react-router";

import useSWR from "swr";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import ListItem from "@mui/material/ListItem";

import { useFilter, useLocalStorage, useSort } from "@tzmedical/react-hooks";

import taskList from "../../../shared/taskList.js";
import { swrFetcher } from "../../axiosClient.js";
import Alert from "../../components/common/Alert.jsx";
import PageLoading from "../../components/common/PageLoading.jsx";
import ToggleButton from "../../components/common/ToggleButton.jsx";
import EditButton from "../../components/EditButton.jsx";
import PLCForm from "../../components/PLCForm.jsx";
import DocumentContext from "../../contexts/DocumentContext.jsx";
import PageContext from "../../contexts/PageContext.jsx";
import SearchContext from "../../contexts/SearchContext.jsx";
import PLCRow from "./PLCRow.jsx";
import PLCsHeader from "./PLCsHeader.jsx";

//-----------------------------------------------------------------------------
// Page configuration
//-----------------------------------------------------------------------------
const DATA_REFRESH_INTERVAL_MS = 15000;
const searchFields = {
  product: "name",
  part: (object) => {
    if (object.parts?.length && Array.isArray(object.parts)) {
      return object.parts.reduce((text, part) => `${text}, ${part.number} - ${part.description}`, "");
    }
    return "";
  },
  engineering: "engineeringRelease",
  launch: (object) =>
    object.isComplete && object.dateOfCompletion ? object.dateOfCompletion : object.launchDate,
  revenue: "thirdYearRevenue",
  is: { completed: (equipment) => equipment.isComplete },
};
const sortOptions = {
  defaultSort: [
    {
      field: "launchDate",
      reverse: false,
    },
  ],
};

//-----------------------------------------------------------------------------
function PLCPage() {
  const location = useLocation();

  //---------------------------------------------------------------------------
  // Hiding completed PLCs
  //---------------------------------------------------------------------------
  const [showCompleted, setShowCompleted] = useLocalStorage(`showCompleted-${location.pathname}`, false);
  const handleDisabledToggle = React.useCallback(() => {
    setShowCompleted((value) => !value);
  }, [setShowCompleted]);

  //---------------------------------------------------------------------------
  // Fetch the data using SWR
  //---------------------------------------------------------------------------
  const {
    data: swrData,
    error: swrError,
    isLoading: swrLoading,
    mutate,
  } = useSWR({ url: "/api/plcs" }, swrFetcher, {
    refreshInterval: DATA_REFRESH_INTERVAL_MS,
  });

  const error = swrError ? swrError.response?.data?.error || swrError.message : null;
  const setError = React.useCallback(
    (val) => {
      if (val === null) {
        mutate();
      }
    },
    [mutate]
  );

  const PLCsList = React.useMemo(() => swrData || [], [swrData]);
  const tableLoading = swrLoading && !PLCsList.length;

  //---------------------------------------------------------------------------
  // Navbar buttons
  //---------------------------------------------------------------------------
  const { setPageButtons } = React.useContext(PageContext);
  React.useEffect(() => {
    setPageButtons(
      <span>
        <ListItem>
          <PLCForm mutate={mutate}>Add Checklist</PLCForm>
        </ListItem>
        <ListItem>
          <EditButton>Edit Data</EditButton>
        </ListItem>
        <ListItem sx={{ mb: 1 }}>
          <ToggleButton
            data-cy="toggle-disabled"
            onClick={handleDisabledToggle}
            enabled={showCompleted}
            enabledIcon={<VisibilityIcon />}
            disabledIcon={<VisibilityOffIcon />}
            enabledMessage="Hide Completed"
            disabledMessage="Show Completed"
          />
        </ListItem>
      </span>
    );
    return () => setPageButtons(null);
  }, [setPageButtons, location, handleDisabledToggle, showCompleted, mutate]);

  //---------------------------------------------------------------------------
  // Search
  //---------------------------------------------------------------------------
  const { search, setSearch } = React.useContext(SearchContext);
  const formattedSearch = React.useMemo(() => {
    return showCompleted ? search : `${search} -is:completed`;
  }, [search, showCompleted]);
  const filteredPLCs = useFilter(PLCsList, formattedSearch, searchFields);
  const [sortedPLCs, handleSortSelection, sort] = useSort(filteredPLCs, sortOptions);
  React.useEffect(() => {
    return () => {
      // Reset the search bar every time we navigate to a new page
      setSearch("");
      mutate();
    };
  }, [setSearch, location, mutate]);

  //---------------------------------------------------------------------------
  // Manual calculation of PLC tasks
  //---------------------------------------------------------------------------
  const { documents, documentIsApproved } = React.useContext(DocumentContext);
  const mergedPLCs = React.useMemo(() => {
    const withDefaultTasks = sortedPLCs.map((plc) => {
      const mergedTaskList = plc.plcTasks.concat(
        taskList.filter((task) => !plc.plcTasks.some((defaultTask) => defaultTask.name === task.name))
      );

      const mergedPlc = {
        ...plc,
        tasks: mergedTaskList.map((task) => {
          if (task.trackedDocument && task.documentNumber) {
            const document = documents?.find((doc) => doc.id === task.documentNumber);
            const statusColor = documentIsApproved(document, task.documentMinimumVersion)
              ? "success"
              : "warning";
            const status = statusColor === "success" ? "Approved" : document?.workflow_state || "Unknown";
            return {
              ...task,
              status,
              statusColor,
              ...(statusColor === "success" && { statusDate: document?.last_approved_date }),
            };
          }
          let statusColor = "warning";
          if (task.status?.includes("Completed")) {
            statusColor = "success";
          } else if (task.status?.includes("Not Applicable")) {
            statusColor = "disabled";
          }
          return {
            status: "To Do",
            statusColor,
            ...(task.trackedDocument && {
              status: "Missing Document",
              statusColor: "error",
            }),
            ...task,
          };
        }),
      };
      mergedPlc.totalTasks = mergedPlc.tasks.length;
      mergedPlc.completedTasks = mergedPlc.tasks.reduce(
        (acc, el) => (["success", "disabled"].includes(el.statusColor) ? acc + 1 : acc),
        0
      );

      return mergedPlc;
    });

    return withDefaultTasks;
  }, [documents, documentIsApproved, sortedPLCs]);

  if (tableLoading && !PLCsList.length) {
    return (
      <>
        <Alert message={error} setMessage={setError} level="error" />
        {!error && <PageLoading />}
      </>
    );
  }

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      <Alert message={error} setMessage={setError} level="error" />
      <PLCsHeader sort={sort} setSort={handleSortSelection} />
      {mergedPLCs.map((plc) => (
        <PLCRow key={plc.id} plc={plc} mutate={mutate} />
      ))}
    </>
  );
}

export default PLCPage;
